/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { logout } from "../../features/user";
import axios from "axios";
import { API_URL, API_OK, API_EXCEPTION, DEFAULT_ERROR_TEXT } from '../../Constants';
import '../../styles.css';
import cookie from "js-cookie";
import { useTranslation } from 'react-i18next';
import { useDispatch } from "react-redux";
import { showErrorDialog } from "../../features/errorDialog";
import { handleApiErrorMessage, formatLocalPhone } from "../../Helper";
import { Dialog, Backdrop, CircularProgress } from '@mui/material';
import CustomerDesktop from './Desktop/Customer';
import CustomerMobile from './Mobile/Customer';

/** how many lines are pulled in one go when the whole list is needed (search / export). */
const ALL_LINES_LIMIT = 20000;
/** page sizes offered for the customer's lines - same range as the customers list. */
const LINE_PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

/** Columns of the lines export file, in the order they are written. */
const EXPORT_COLUMNS = [
  { key: 'use_name', label: 'שם המנוי' },
  { key: 'number', label: 'מספר', format: (value) => formatLocalPhone(value) },
  { key: 'id', label: 'מזהה מנוי' },
  {
    key: 'membership', label: 'שיוך לקהילה',
    format: (value) => (value === 'LINE_LINK' ? 'משויך למנוי' : (value === 'CUSTOMER' ? 'דרך הלקוח' : ''))
  },
  { key: 'create_date', label: 'תאריך הצטרפות' },
  { key: 'last_call', label: 'שיחה אחרונה' },
];

/** Quote one CSV cell; a leading =/+/-/@ is escaped so Excel doesn't read it as a formula. */
const toCsvCell = (value) => {
  let text = (value === null || value === undefined) ? '' : String(value);
  if(/^[=+\-@]/.test(text)){
    text = "'" + text;
  }
  return '"' + text.replace(/"/g, '""') + '"';
};

export default function Customer(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortColumn, setSortColumn] = useState("create_date");
  const [sortColumnOrder, setSortColumnOrder] = useState("asc");

  const [expandedRowIds, setExpandedRowIds] = useState([]);

  /* -- the customer's full lines list --
     the API pages the lines, but searching and exporting need all of them, so the whole
     list is pulled once and then filtered / sorted / paged on the client. */
  const [allLines, setAllLines] = useState(null);
  const [allLinesLoading, setAllLinesLoading] = useState(false);
  const allLinesRequest = useRef(null);   // the in-flight fetch, so typing doesn't start several
  const [exporting, setExporting] = useState(false);

  const searchTerm = (props.lineSearch || '').trim();

  /* ── remove-line-from-community dialog state ── */
  const initialLineToRemove = { id: null, use_name: '', number: '', membership: null };
  const [lineToRemove, setLineToRemove] = useState(initialLineToRemove);
  const [removeDialogIsOpen, setRemoveDialogIsOpen] = useState(false);
  const [removeNote, setRemoveNote] = useState('');
  const [missingRemoveNote, setMissingRemoveNote] = useState(false);
  const [removeDialogLoaderVisible, setRemoveDialogLoaderVisible] = useState(false);

  useEffect(() => {
    if(searchTerm !== ''){
      ensureAllLines();   // searching works on the full list, not on a server page
      return () => {};
    }
    if(loading){
      return () => {};
    }
    refreshLinesData();
  }, [page, pageSize, sortColumn, sortColumnOrder, searchTerm]);

  // a changed search term always starts over at the first page
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // another customer means another lines list
  useEffect(() => {
    setAllLines(null);
    allLinesRequest.current = null;
  }, [props.customer.id]);


  useEffect(() => {
    document.body.addEventListener('click', (e) => {
        if(e.target.closest(".body") !== null
        || e.target.closest(".header") !== null
        || e.target.closest(".expansions-wrapper") !== null
        || e.target.closest("div[role='presentation']") !== null
        ){
        return;
      }else{
        setExpandedRowIds([]);
      }
    })
  }, []);


  const refreshLinesData = (pageToStart = null) => {
    if(pageToStart === null){
      pageToStart = page;
    }
    let errorMessage = t(DEFAULT_ERROR_TEXT);
    setLoading(true);

    let  headers = {
      token: cookie.get('token')
    }

    axios.post(API_URL + '/getLinesInCustomers',
      {
        customer_id: props.customer.id,
        start: pageToStart*pageSize,
        limit: pageSize,
        sort_column: sortColumn,
        sort_order: sortColumnOrder
      },
      {headers})
    .then(
      ({ data }) => {
        if(data.hasOwnProperty("responseStatus") && (data["responseStatus"] === API_OK && data.hasOwnProperty("lines"))){
          setRows(data.lines);
          setRowCount(data.totalCount);
          setLoading(false);
        }else{
          if((data.responseStatus === API_EXCEPTION) && (data.hasOwnProperty("message"))){
            errorMessage = data["message"];
          }
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
          setLoading(false);
        }
      }).catch((error) => {
        handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
        setLoading(false);
    });
  };


  /** Pull every line of the customer (one request, no paging). Throws on an API error. */
  const fetchAllLines = async () => {
    const headers = { token: cookie.get('token') };
    const { data } = await axios.post(API_URL + '/getLinesInCustomers',
      {
        customer_id: props.customer.id,
        start: 0,
        limit: ALL_LINES_LIMIT,
        sort_column: sortColumn,
        sort_order: sortColumnOrder
      },
      { headers });
    if(data && data.responseStatus === API_OK && Array.isArray(data.lines)){
      return data.lines;
    }
    throw new Error((data && data.responseStatus === API_EXCEPTION && data.message) ? data.message : t(DEFAULT_ERROR_TEXT));
  };

  /** The cached full list, fetching it on the first call. Resolves to null when the fetch failed. */
  const ensureAllLines = () => {
    if(allLines !== null){
      return Promise.resolve(allLines);
    }
    if(allLinesRequest.current !== null){
      return allLinesRequest.current;
    }
    setAllLinesLoading(true);
    const request = fetchAllLines()
      .then((lines) => {
        setAllLines(lines);
        return lines;
      })
      .catch((error) => {
        handleApiErrorMessage(error.message, t, dispatch, showErrorDialog, logout, navigate);
        return null;
      })
      .finally(() => {
        allLinesRequest.current = null;
        setAllLinesLoading(false);
      });
    allLinesRequest.current = request;
    return request;
  };

  /** Reload after a change - refreshes whichever list is currently on screen. */
  const reloadLines = async () => {
    setAllLines(null);
    allLinesRequest.current = null;
    if(searchTerm === ''){
      refreshLinesData();
      return;
    }
    setAllLinesLoading(true);
    try {
      setAllLines(await fetchAllLines());
    } catch (error) {
      handleApiErrorMessage(error.message, t, dispatch, showErrorDialog, logout, navigate);
    } finally {
      setAllLinesLoading(false);
    }
  };

  /* -- search: match a line by its name, its number (0 <-> 972) or its id -- */
  const searchNumberCandidates = (() => {
    const digits = searchTerm.replace(/[^0-9]/g, '');
    if(!digits){
      return [];
    }
    const candidates = [digits];
    if(digits.startsWith('0')){
      candidates.push('972' + digits.slice(1));
    }else if(digits.startsWith('972')){
      candidates.push('0' + digits.slice(3));
    }
    return candidates;
  })();

  const matchesSearch = (line) => {
    if(searchTerm === ''){
      return true;
    }
    if(String(line.use_name || '').toLowerCase().includes(searchTerm.toLowerCase())){
      return true;
    }
    const number = String(line.number || '');
    if(searchNumberCandidates.some((candidate) => number.includes(candidate))){
      return true;
    }
    return String(line.id || '').includes(searchTerm);
  };

  /** Order two lines by the selected sort column (the client-side list isn't server-sorted). */
  const compareLines = (a, b) => {
    const direction = (sortColumnOrder === 'desc') ? -1 : 1;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if(sortColumn === 'create_date' || sortColumn === 'last_call'){
      const aTime = aValue ? new Date(aValue).getTime() : 0;
      const bTime = bValue ? new Date(bValue).getTime() : 0;
      if(!isNaN(aTime) && !isNaN(bTime)){
        return (aTime - bTime) * direction;
      }
    }
    return String(aValue ?? '').localeCompare(String(bValue ?? ''), 'he', { numeric: true }) * direction;
  };

  const filteredLines = (searchTerm !== '' && allLines !== null)
    ? allLines.filter(matchesSearch).sort(compareLines)
    : null;
  const currentPageSize = Number(pageSize) || 50;
  const displayRows = (filteredLines !== null)
    ? filteredLines.slice(page*currentPageSize, (page+1)*currentPageSize)
    : ((searchTerm !== '') ? [] : rows);
  const displayRowCount = (filteredLines !== null) ? filteredLines.length : ((searchTerm !== '') ? 0 : rowCount);

  /**
   * Export the customer's lines to a CSV file, built here in the browser - the API has no
   * per-customer lines export. When a search is active only the matching lines are written.
   */
  const exportLines = async () => {
    if(exporting){
      return;
    }
    setExporting(true);
    const lines = await ensureAllLines();
    if(lines === null){
      setExporting(false);
      return;
    }

    const linesToExport = lines.filter(matchesSearch).sort(compareLines);
    const csv = [EXPORT_COLUMNS.map((column) => toCsvCell(column.label)).join(',')]
      .concat(linesToExport.map((line) => EXPORT_COLUMNS
        .map((column) => toCsvCell(column.format ? column.format(line[column.key]) : line[column.key]))
        .join(',')))
      .join('\r\n');

    // the BOM makes Excel open the Hebrew columns as UTF-8
    const url = window.URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }));
    const safeName = String(props.customer.name || props.customer.id || 'customer').replace(/[\\/:*?"<>|]/g, '_').trim();
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'lines_' + (safeName || 'customer') + '_' + new Date().getTime() + '.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
    setExporting(false);
  };

  const handleViewWatchProfile = (cellValues) => {
    props.setSelectedLine({
      id: cellValues.id,
      number: cellValues.number,
      use_name: cellValues.use_name
    });
  };

  /* ── remove a single line from the community ── */
  const openRemoveLine = (line) => {
    setLineToRemove({ id: line.id, use_name: line.use_name, number: line.number, membership: line.membership });
    setRemoveNote('');
    setMissingRemoveNote(false);
    setRemoveDialogIsOpen(true);
  };

  const closeRemoveLine = () => {
    setRemoveDialogIsOpen(false);
    setMissingRemoveNote(false);
  };

  const handleRemoveLineSubmit = () => {
    if (removeNote === "") {
      setMissingRemoveNote(true);
      return;
    }
    setMissingRemoveNote(false);

    let errorMessage = t(DEFAULT_ERROR_TEXT);
    setRemoveDialogLoaderVisible(true);

    const headers = { token: cookie.get('token') };
    const data = {
      customer_id: props.customer.id,
      line_id: lineToRemove.id,
      note: removeNote
    };

    axios.post(API_URL + '/outLine', data, { headers })
      .then((responseData) => {
        if (responseData.data.hasOwnProperty('responseStatus') && responseData.data.responseStatus === API_OK) {
          setRemoveDialogLoaderVisible(false);
          setRemoveDialogIsOpen(false);
          setLineToRemove(initialLineToRemove);
          reloadLines();
        } else {
          if (responseData.data.responseStatus === API_EXCEPTION && responseData.data.hasOwnProperty("message")) {
            errorMessage = responseData.data["message"];
          }
          handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
          setRemoveDialogLoaderVisible(false);
        }
      }).catch(() => {
        handleApiErrorMessage(errorMessage, t, dispatch, showErrorDialog, logout, navigate);
        setRemoveDialogLoaderVisible(false);
      });
  };

  return (<>
    {(props.isDesktop ?
    <CustomerDesktop
      customer={props.customer}
      setCustomerView={props.setCustomerView}
      selectedLine={props.selectedLine}
      resetSelectedLine={props.resetSelectedLine}
      setSelectedLine={props.setSelectedLine}
      rowCount={displayRowCount}
      rows={displayRows}
      loading={(searchTerm !== '') ? allLinesLoading : loading}
      page={page}
      pageSize={pageSize}
      pageSizeOptions={LINE_PAGE_SIZE_OPTIONS}
      setPage={setPage}
      setPageSize={setPageSize}
      sortColumn={sortColumn}
      setSortColumn={setSortColumn}
      sortColumnOrder={sortColumnOrder}
      setSortColumnOrder={setSortColumnOrder}
      handleViewWatchProfile={handleViewWatchProfile}
      handleViewCustomerWatchProfile={props.handleViewCustomerWatchProfile}
      expandedRowIds={expandedRowIds}
      setExpandedRowIds={setExpandedRowIds}
      onRequestRemoveLine={openRemoveLine}
      onExportLines={exportLines}
      exporting={exporting}
      lineSearch={searchTerm}

    /> :
    <CustomerMobile
      customer={props.customer}
      setCustomerView={props.setCustomerView}
      rowCount={displayRowCount}
      rows={displayRows}
      loading={(searchTerm !== '') ? allLinesLoading : loading}
      page={page}
      pageSize={pageSize}
      pageSizeOptions={LINE_PAGE_SIZE_OPTIONS}
      setPage={setPage}
      setPageSize={setPageSize}
      sortColumn={sortColumn}
      setSortColumn={setSortColumn}
      sortColumnOrder={sortColumnOrder}
      setSortColumnOrder={setSortColumnOrder}
      handleViewWatchProfile={handleViewWatchProfile}
      handleViewCustomerWatchProfile={props.handleViewCustomerWatchProfile}
      expandedRowIds={expandedRowIds}
      setExpandedRowIds={setExpandedRowIds}
      onRequestRemoveLine={openRemoveLine}
      onExportLines={exportLines}
      exporting={exporting}
      lineSearch={searchTerm}
    />)}

    <Dialog open={removeDialogIsOpen} onClose={closeRemoveLine} PaperProps={{ className: 'bl-dialog-paper' }}>
      <div className='bl-dialog'>
        <div className='bl-dialog-header bl-dialog-header--danger'>
          <div className='bl-dialog-icon'>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/></svg>
          </div>
          <h3 className='bl-dialog-title'>הסרת מנוי מהקהילה</h3>
          <button className='bl-dialog-close' onClick={closeRemoveLine}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className='bl-dialog-body'>
          <p className='cat-join-text'>המנוי <strong>{lineToRemove.use_name || formatLocalPhone(lineToRemove.number) || lineToRemove.number}</strong> עומד להיות מוסר מהקהילה.</p>
          <div className='cat-join-warning'>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span>
              שימו לב!! פעולה זו הינה <strong>בלתי הפיכה</strong>.
              {lineToRemove.membership === 'CUSTOMER'
                ? ' מנוי זה משויך לקהילה דרך הלקוח - הסרתו עלולה להסיר את שיוך הלקוח לקהילה או להעביר את המנוי לקהילת ברירת המחדל, ולהשפיע על מנויים נוספים של הלקוח.'
                : ''}
            </span>
          </div>
          <div className='bl-field'>
            <label htmlFor="line-removal-note">{t('Reason for removal')}</label>
            <input id="line-removal-note" type="text" className='bl-input'
              placeholder={t('Reason for removal')}
              value={removeNote}
              onChange={(e) => setRemoveNote(e.target.value)}
            />
            {missingRemoveNote && <div className='bl-field-error'>* {t("Required field")}</div>}
          </div>
        </div>
        <div className='bl-dialog-footer'>
          <button className='bl-dialog-cancel' onClick={closeRemoveLine}>{t('Cancel')}</button>
          <button className='bl-dialog-submit bl-dialog-submit--danger' onClick={handleRemoveLineSubmit}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            {t("Remove")}
          </button>
        </div>
        <Backdrop
          sx={{ color: '#fff', position: 'absolute', borderRadius: '18px', zIndex: 5 }}
          open={removeDialogLoaderVisible}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </Dialog>
    </>
  );
}
